import { Icon } from '@iconify/react'
import { useState } from 'react'

type BrowserName = 'chrome' | 'brave' | 'firefox' | 'edge'

const browserTabs: Array<{
  key: BrowserName
  label: string
  icon: string
  extensionsUrl: string
  details: string
  steps: string[]
}> = [
  {
    key: 'chrome',
    label: 'Chrome',
    icon: 'simple-icons:googlechrome',
    extensionsUrl: 'chrome://extensions',
    details: 'Download ZIP, load unpacked in chrome://extensions.',
    steps: [
      'Download the ZIP below.',
      'Extract the ZIP to a folder on your computer.',
      'Open chrome://extensions in a new tab.',
      'Enable "Developer mode" (toggle top-right).',
      'Click "Load unpacked" and select the extracted folder.',
    ],
  },
  {
    key: 'brave',
    label: 'Brave',
    icon: 'simple-icons:brave',
    extensionsUrl: 'brave://extensions',
    details: 'Download ZIP, load unpacked in brave://extensions.',
    steps: [
      'Download the ZIP below.',
      'Extract the ZIP to a folder on your computer.',
      'Open brave://extensions in a new tab.',
      'Enable "Developer mode" (toggle top-right).',
      'Click "Load unpacked" and select the extracted folder.',
    ],
  },
  {
    key: 'firefox',
    label: 'Firefox',
    icon: 'simple-icons:firefoxbrowser',
    extensionsUrl: 'about:debugging#/runtime/this-firefox',
    details: 'Download ZIP, load as temporary add-on.',
    steps: [
      'Download the ZIP below.',
      'Extract the ZIP to a folder on your computer.',
      'Open about:debugging#/runtime/this-firefox in a new tab.',
      'Click "Load Temporary Add-on" and select the manifest.json in the extracted folder.',
    ],
  },
  {
    key: 'edge',
    label: 'Edge',
    icon: 'simple-icons:microsoftedge',
    extensionsUrl: 'edge://extensions',
    details: 'Download ZIP, load unpacked in edge://extensions.',
    steps: [
      'Download the ZIP below.',
      'Extract the ZIP to a folder on your computer.',
      'Open edge://extensions in a new tab.',
      'Enable "Developer mode" (toggle bottom-left).',
      'Click "Load unpacked" and select the extracted folder.',
    ],
  },
]

const autoUpdateGuides = {
  windows: {
    title: 'Windows (Scheduled Task)',
    oneLiner: 'irm __BASE_URL__/api/v1/extension/install-chrome.ps1 | iex',
    steps: [
      'Run the one-liner above in an elevated PowerShell prompt (Right-click Start → Terminal (Admin)).',
      'This downloads the extension, extracts it to <code>%USERPROFILE%\\MyScope</code>, and registers a daily scheduled task for auto-updates.',
      'Go to <code>chrome://extensions</code>, enable Developer mode, click "Load unpacked", and select <code>%USERPROFILE%\\MyScope</code>.',
      'On future updates, the scheduled task downloads and extracts the latest ZIP automatically. Reload the extension or restart the browser.',
    ],
  },
  linux: {
    title: 'Linux (Cron)',
    steps: [
      'Download and extract the ZIP manually, or run the setup script:',
      '<code>mkdir -p ~/MyScope && cd ~/MyScope</code>',
      '<code>curl -Lo myscope.zip __BASE_URL__/api/v1/extension/download</code>',
      '<code>unzip -o myscope.zip && rm myscope.zip && chmod +x update-extension.sh</code>',
      'Open <code>chrome://extensions</code> (or <code>brave://extensions</code>), enable Developer mode, click "Load unpacked", select <code>~/MyScope</code>.',
      'Schedule daily auto-updates via cron: <code>crontab -e</code>, add:',
      '<code>0 10 * * * $HOME/MyScope/update-extension.sh</code>',
      'Or use the included script: <code>./update-extension.sh --install-cron</code>',
    ],
  },
  mac: {
    title: 'macOS (Cron / launchd)',
    steps: [
      'Download and extract the ZIP manually, or run:',
      '<code>mkdir -p ~/MyScope && cd ~/MyScope</code>',
      '<code>curl -Lo myscope.zip __BASE_URL__/api/v1/extension/download</code>',
      '<code>unzip -o myscope.zip && rm myscope.zip && chmod +x update-extension.sh</code>',
      'Open <code>chrome://extensions</code> (or <code>brave://extensions</code>), enable Developer mode, click "Load unpacked", select <code>~/MyScope</code>.',
      'Schedule daily auto-updates via: <code>./update-extension.sh --install-cron</code>',
      'Or manually add a cron job: <code>0 10 * * * $HOME/MyScope/update-extension.sh</code>',
    ],
  },
} as const

const osKeys = ['windows', 'linux', 'mac'] as const

function AutoUpdateSection() {
  const [activeOs, setActiveOs] = useState<string>('windows')

  const baseUrl = typeof window !== 'undefined'
    ? (document.querySelector<HTMLMetaElement>('meta[name="server-url"]')?.content || window.location.origin)
    : 'https://YOUR_SERVER'

  const os = autoUpdateGuides[activeOs as keyof typeof autoUpdateGuides]

  return (
    <div className="mt-6 rounded-lg border border-purple-700/40 bg-purple-950/20 p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-purple-400">
        Auto-update setup
      </h3>
      <p className="mt-1 text-xs text-slate-400">
        Set up a scheduled task or cron job to automatically download and extract
        the latest extension ZIP. After each update, reload the extension or restart
        the browser.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {osKeys.map((key) => (
          <button
            key={key}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeOs === key
                ? 'border-purple-500 bg-purple-800/50 text-purple-200'
                : 'border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            onClick={() => setActiveOs(key)}
          >
            {autoUpdateGuides[key].title}
          </button>
        ))}
      </div>

      {activeOs === 'windows' && (
        <div className="mt-4 rounded-lg border border-green-700/50 bg-green-900/20 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-green-400">One-liner (run in elevated PowerShell):</p>
              <pre className="mt-2 overflow-x-auto text-sm text-green-300">
                {autoUpdateGuides.windows.oneLiner.replace('__BASE_URL__', baseUrl)}
              </pre>
              <button
                className="mt-1 text-xs text-green-400 underline hover:text-green-300"
                onClick={() => navigator.clipboard.writeText(
                  autoUpdateGuides.windows.oneLiner.replace('__BASE_URL__', baseUrl)
                )}
              >
                Copy to clipboard
              </button>
            </div>
            <a
              href="/api/v1/extension/install-chrome.ps1"
              download
              className="shrink-0 rounded-lg border border-green-600 bg-green-800/30 px-3 py-1.5 text-xs font-medium text-green-300 transition-colors hover:bg-green-700/40"
            >
              ⬇ Script
            </a>
          </div>
        </div>
      )}

      <ol className="mt-4 list-inside list-decimal space-y-1.5 text-sm text-slate-300">
        {os.steps.map((step, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: step }} />
        ))}
      </ol>

      <div className="mt-4 rounded-lg border border-amber-700/50 bg-amber-900/20 p-3">
        <p className="text-xs text-amber-300">
          <strong>After updating:</strong> Go to <code>chrome://extensions</code> and click the
          refresh icon on MyScope Scraper, or restart the browser. The extension auto-updates
          in the background — the browser just needs to pick up the new files.
        </p>
      </div>
    </div>
  )
}

export function InstallPage() {
  const [activeBrowserTab, setActiveBrowserTab] = useState<BrowserName>('chrome')
  const [userId, setUserId] = useState('')

  const tab = browserTabs.find((x) => x.key === activeBrowserTab) ?? browserTabs[0]
  const downloadUrl = `/api/v1/extension/download${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-4xl p-6">
        <section className="panel">
          <h1 className="text-2xl font-semibold">Extension required</h1>
          <p className="mt-2 text-slate-300">
            Install the MyScope browser extension, then{' '}
            <a href="/" className="text-cyan-400 underline">reload the panel</a>.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {browserTabs.map((item) => (
              <button
                key={item.key}
                className={`browser-tab ${activeBrowserTab === item.key ? 'browser-tab--active' : ''}`}
                onClick={() => setActiveBrowserTab(item.key)}
              >
                <Icon icon={item.icon} width="18" height="18" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <div className="mt-4 rounded-xl border border-slate-700 bg-slate-900 p-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
              <Icon icon={tab.icon} width="20" height="20" />
              {tab.label} install
            </h2>
            <p className="mt-2 text-sm text-slate-300">{tab.details}</p>

            <div className="mt-4 flex items-center gap-2">
              <input
                type="text"
                placeholder="Your User ID (optional)"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="flex-1 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 placeholder-slate-500"
              />
              <a
                className="btn shrink-0"
                href={downloadUrl}
                download
              >
                Download ZIP
              </a>
            </div>

            <div className="mt-4 rounded-lg border border-slate-600 bg-slate-800/50 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
                Load unpacked — step by step
              </h3>
              <ol className="mt-3 list-inside list-decimal space-y-1.5 text-sm text-slate-300">
                {tab.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              <div className="mt-4 rounded-lg border border-amber-700/50 bg-amber-900/20 p-3">
                <p className="text-xs text-amber-300">
                  <strong>First time?</strong> After installing, open the extension popup and set your
                  User ID in the settings. All scraped items will be attributed to that ID.
                </p>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-400">
                <Icon icon="mdi:alert" width="14" height="14" />
                <span>
                  After loading, reload this page. Extension handshake happens automatically.
                </span>
              </div>
            </div>

            {activeBrowserTab !== 'firefox' && <AutoUpdateSection />}
          </div>
        </section>
      </div>
    </div>
  )
}
